/**
 * Display formatting for timestamps.
 *
 * The API stores and returns UTC (`timestamp with time zone`, serialised with a `Z`). The browser
 * would happily convert that to whatever the *device* is set to — which is why a server or kiosk
 * left on UTC shows UTC on screen, and two terminals in the same pharmacy can disagree.
 *
 * So the display zone is pinned to one configured value instead of the device's. A hospital runs in
 * one place; the time on a prescription queue should read the same on every screen in the building,
 * whatever each machine's clock is set to.
 *
 * Change it per deployment with `VITE_DISPLAY_TIMEZONE` (an IANA name, e.g. "Asia/Kolkata",
 * "Asia/Dubai", "Europe/London"). Set it to "local" to fall back to the device's own zone.
 */

const CONFIGURED = (import.meta.env.VITE_DISPLAY_TIMEZONE as string | undefined)?.trim();

/** IANA zone used for every timestamp on screen. `undefined` = use the device's zone. */
export const DISPLAY_TIMEZONE: string | undefined =
  !CONFIGURED || CONFIGURED.toLowerCase() === "local" ? "Asia/Kolkata" : CONFIGURED;

const LOCALE = "en-IN";

/**
 * A `DateTime` that reached us without a UTC marker would be read as device-local and silently
 * shifted. The API sends `Z`, but a hand-built payload or an older endpoint might not — treat a
 * bare `yyyy-MM-ddTHH:mm:ss` as the UTC it actually is.
 */
function parse(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

  const s = value.trim();
  if (!s) return null;
  const hasZone = /[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
  const d = new Date(hasZone || !s.includes("T") ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

const dateTimeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DISPLAY_TIMEZONE,
  day: "2-digit", month: "short", year: "numeric",
  hour: "2-digit", minute: "2-digit", hour12: true,
});

const dateFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DISPLAY_TIMEZONE,
  day: "2-digit", month: "short", year: "numeric",
});

const timeFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: DISPLAY_TIMEZONE,
  hour: "2-digit", minute: "2-digit", hour12: true,
});

/** "11 Aug 2026, 03:56 pm" — the default for anything with a time component. */
export function formatDateTime(value: string | Date | null | undefined, fallback = ""): string {
  const d = parse(value);
  return d ? dateTimeFmt.format(d) : fallback;
}

/** A bare `yyyy-MM-dd` — a calendar date with no instant behind it. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Same fields as `dateFmt`, but anchored to UTC so a date-only value is never shifted. */
const dateOnlyFmt = new Intl.DateTimeFormat(LOCALE, {
  timeZone: "UTC",
  day: "2-digit", month: "short", year: "numeric",
});

/**
 * "11 Aug 2026".
 *
 * A value the API sends as a plain date (`DateOnly` — an expiry, a date of birth, a period end)
 * carries no time and no zone, so it must be printed exactly as sent. Converting it would move an
 * expiry of 2027-03-01 to 28 Feb for any tenant behind UTC. Only real instants get converted.
 */
export function formatDate(value: string | Date | null | undefined, fallback = ""): string {
  if (typeof value === "string" && DATE_ONLY.test(value.trim())) {
    const d = new Date(`${value.trim()}T00:00:00Z`);
    return Number.isNaN(d.getTime()) ? fallback : dateOnlyFmt.format(d);
  }
  const d = parse(value);
  return d ? dateFmt.format(d) : fallback;
}

/**
 * "Mar 2027" — a month and year, the way an expiry is printed on a pack.
 * Anchored to UTC like `formatDate`, since an expiry is a calendar date, not an instant.
 */
export function formatMonthYear(value: string | Date | null | undefined, fallback = ""): string {
  const fmt = new Intl.DateTimeFormat(LOCALE, {
    timeZone: typeof value === "string" && DATE_ONLY.test(value.trim()) ? "UTC" : DISPLAY_TIMEZONE,
    month: "short", year: "numeric",
  });
  const d = typeof value === "string" && DATE_ONLY.test(value.trim())
    ? new Date(`${value.trim()}T00:00:00Z`)
    : parse(value);
  return d && !Number.isNaN(d.getTime()) ? fmt.format(d) : fallback;
}

/** "Tuesday, August 11" — the dashboard greeting line. */
export function formatLongDate(value: string | Date | null | undefined, fallback = ""): string {
  const d = parse(value);
  if (!d) return fallback;
  return new Intl.DateTimeFormat(LOCALE, {
    timeZone: DISPLAY_TIMEZONE,
    weekday: "long", month: "long", day: "numeric",
  }).format(d);
}

/** "03:56 pm" — when the date is already obvious from context. */
export function formatTime(value: string | Date | null | undefined, fallback = ""): string {
  const d = parse(value);
  return d ? timeFmt.format(d) : fallback;
}

/**
 * "2 min ago" / "3 h ago" — for queue rows where "how long has this been waiting" is the actual
 * question. Falls back to the absolute timestamp past a week, where elapsed time stops being useful.
 */
export function formatRelative(value: string | Date | null | undefined, fallback = ""): string {
  const d = parse(value);
  if (!d) return fallback;

  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days} d ago`;
  return dateFmt.format(d);
}

/** How far the display zone is ahead of UTC at that instant, in ms. Handles DST. */
function zoneOffsetMs(at: Date): number {
  if (!DISPLAY_TIMEZONE) return -at.getTimezoneOffset() * 60_000;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: DISPLAY_TIMEZONE, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(at);
  const f: Record<string, number> = {};
  for (const p of parts) if (p.type !== "literal") f[p.type] = Number(p.value);
  // Re-read the zone's wall clock as if it were UTC; the gap is the offset.
  const asUtc = Date.UTC(f.year, f.month - 1, f.day, f.hour % 24, f.minute, f.second);
  return asUtc - at.getTime();
}

/**
 * Turn a `yyyy-MM-dd` picked in a date input into the UTC instant of that day's **start** in the
 * display zone. Without this a filter for "today" would run UTC midnight to UTC midnight — in
 * IST that is 05:30 to 05:30, so the first five and a half hours of the day go missing from the
 * results while the screen shows them as today.
 */
export function startOfDayUtc(yyyyMmDd: string): string | undefined {
  return dayBoundaryUtc(yyyyMmDd, 0, 0, 0, 0);
}

/** The same for the day's **end** (23:59:59.999 in the display zone), for an inclusive range. */
export function endOfDayUtc(yyyyMmDd: string): string | undefined {
  return dayBoundaryUtc(yyyyMmDd, 23, 59, 59, 999);
}

function dayBoundaryUtc(
  yyyyMmDd: string, h: number, mi: number, s: number, ms: number,
): string | undefined {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(yyyyMmDd?.trim() ?? "");
  if (!m) return undefined;
  const [, y, mo, d] = m;
  const naive = new Date(Date.UTC(+y, +mo - 1, +d, h, mi, s, ms));
  // Offset is resolved at the guess, then re-checked — a DST boundary can shift it by an hour.
  const first = new Date(naive.getTime() - zoneOffsetMs(naive));
  const exact = new Date(naive.getTime() - zoneOffsetMs(first));
  return exact.toISOString();
}

/** Short zone label for a header, e.g. "IST" — so staff know which clock they are reading. */
export function displayTimezoneLabel(): string {
  try {
    const parts = new Intl.DateTimeFormat(LOCALE, {
      timeZone: DISPLAY_TIMEZONE, timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find(p => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return "";
  }
}
